import React, { useEffect, useState, useMemo } from 'react';
import {
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { ReportLayout, exportToCsv, useCurrentMonthDates } from './ReportLayout';
import { reportsApi } from '../../api/reports';

const columns: GridColDef[] = [
  {
    field: 'worker_name',
    headerName: 'Worker',
    flex: 1,
    minWidth: 150,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <Typography
          variant="body2"
          sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: 'primary.dark' } }}
        >
          {params.value}
        </Typography>
      </Box>
    ),
  },
  { field: 'worker_type', headerName: 'Type', width: 100 },
  { field: 'total_assigned', headerName: 'Assigned', type: 'number', width: 100 },
  { field: 'total_received', headerName: 'Received', type: 'number', width: 100 },
  { field: 'total_defects', headerName: 'Defects', type: 'number', width: 90 },
  { field: 'efficiency', headerName: 'Efficiency %', type: 'number', width: 110 },
  { field: 'defect_rate', headerName: 'Defect %', type: 'number', width: 100 },
  { field: 'gross_earnings', headerName: 'Gross Earnings', type: 'number', width: 130,
    valueFormatter: (value: number) => `₹${(value || 0).toLocaleString('en-IN')}`,
  },
  { field: 'advance_paid', headerName: 'Advance', type: 'number', width: 100,
    valueFormatter: (value: number) => `₹${(value || 0).toLocaleString('en-IN')}`,
  },
  { field: 'net_payable', headerName: 'Net Payable', type: 'number', width: 120,
    valueFormatter: (value: number) => `₹${(value || 0).toLocaleString('en-IN')}`,
  },
];

interface WorkerDetail {
  worker_id: string;
  worker_name: string;
  worker_type: string;
  summary: {
    total_assigned: number;
    total_received: number;
    total_defects: number;
    gross_earnings: number;
    advance_paid: number;
    net_payable: number;
  };
  details: Array<{
    assignment_id: number;
    design_no: string;
    garment_part: string;
    size: string;
    job_type: string;
    quantity_issued: number;
    quantity_received: number;
    defect_quantity: number;
    rate: number;
    earnings: number;
    issue_date: string;
    last_receive_date: string | null;
    status: string;
  }>;
}

export default function WorkerPerformanceReport() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { startDate, endDate, setStartDate, setEndDate } = useCurrentMonthDates();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [jobType, setJobType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState<WorkerDetail | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportsApi.getWorkerPerformance({
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        job_type: jobType || undefined,
      });
      setData(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, jobType]);

  // Filter data by search query
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return data;
    const q = searchQuery.toLowerCase().trim();
    return data.filter(
      (row) =>
        row.worker_name?.toLowerCase().includes(q) ||
        row.worker_id?.toLowerCase().includes(q)
    );
  }, [data, searchQuery]);

  // Handle row click — fetch worker detail
  const handleRowClick = async (params: any) => {
    const workerId = params.row.worker_id;
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailData(null);
    try {
      const res = await reportsApi.getWorkerPerformanceDetail(workerId, {
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      });
      setDetailData(res.data);
    } catch (err: any) {
      setDetailData(null);
    } finally {
      setDetailLoading(false);
    }
  };

  // Group detail rows by design_no for summary
  const designGroups = useMemo(() => {
    if (!detailData?.details) return [];
    const map = new Map<string, {
      design_no: string;
      garment_part: string;
      sizes: typeof detailData.details;
      totalReceived: number;
      totalDefects: number;
      totalEarnings: number;
    }>();

    for (const d of detailData.details) {
      const key = `${d.design_no}__${d.garment_part}`;
      if (!map.has(key)) {
        map.set(key, {
          design_no: d.design_no,
          garment_part: d.garment_part,
          sizes: [],
          totalReceived: 0,
          totalDefects: 0,
          totalEarnings: 0,
        });
      }
      const g = map.get(key)!;
      g.sizes.push(d);
      g.totalReceived += d.quantity_received;
      g.totalDefects += d.defect_quantity;
      g.totalEarnings += d.earnings;
    }

    return Array.from(map.values()).sort((a, b) => b.totalEarnings - a.totalEarnings);
  }, [detailData]);

  const handlePrint = () => {
    if (!detailData) return;

    const monthName = startDate
      ? startDate.format('MMMM_YYYY')
      : 'Report';
    const pdfTitle = `${detailData.worker_name}_${monthName}`.replace(/\s+/g, '_');

    const fmt = (v: number) => `₹${v.toLocaleString('en-IN')}`;

    // Build summary HTML
    const summaryHtml = `
      <div class="summary-row">
        <div class="summary-chip"><div class="label">Received</div><div class="val">${detailData.summary.total_received}</div></div>
        <div class="summary-chip"><div class="label">Defects</div><div class="val">${detailData.summary.total_defects}</div></div>
        <div class="summary-chip"><div class="label">Gross</div><div class="val">${fmt(detailData.summary.gross_earnings)}</div></div>
        <div class="summary-chip"><div class="label">Advance</div><div class="val">${fmt(detailData.summary.advance_paid)}</div></div>
        <div class="summary-chip"><div class="label">Net Payable</div><div class="val">${fmt(detailData.summary.net_payable)}</div></div>
      </div><hr/>`;

    // Build design-wise tables
    let serial = 0;
    const designsHtml = designGroups.map((group) => {
      const rowsHtml = group.sizes.map((s, sIdx) => {
        serial++;
        return `<tr>
          <td>${serial}</td>
          <td>${s.size}</td>
          <td>${formatDate(s.issue_date)}</td>
          <td>${s.last_receive_date ? formatDate(s.last_receive_date) : '-'}</td>
          <td class="right">${s.quantity_issued}</td>
          <td class="right">${s.quantity_received}</td>
          <td class="right">${s.defect_quantity > 0 ? s.defect_quantity : '-'}</td>
          <td class="right">${s.rate > 0 ? s.rate : '-'}</td>
          <td class="right">${s.earnings > 0 ? fmt(s.earnings) : '-'}</td>
        </tr>`;
      }).join('');

      const subtotalHtml = group.sizes.length > 1 ? `<tr class="bold">
        <td></td><td>Total</td><td></td><td></td>
        <td class="right">${group.sizes.reduce((s, r) => s + r.quantity_issued, 0)}</td>
        <td class="right">${group.totalReceived}</td>
        <td class="right">${group.totalDefects > 0 ? group.totalDefects : '-'}</td>
        <td class="right">-</td>
        <td class="right">${fmt(group.totalEarnings)}</td>
      </tr>` : '';

      return `
        <div class="design-header">
          <span class="dno">${group.design_no}</span>
          <span class="part">${group.garment_part}</span>
          <span class="meta">${group.totalReceived} pcs · ${fmt(group.totalEarnings)}</span>
        </div>
        <table>
          <thead><tr>
            <th>#</th><th>Size</th><th>Issue Date</th><th>Receive Date</th>
            <th class="right">Issued</th><th class="right">Received</th><th class="right">Defects</th>
            <th class="right">Rate (₹)</th><th class="right">Earnings (₹)</th>
          </tr></thead>
          <tbody>${rowsHtml}${subtotalHtml}</tbody>
        </table>`;
    }).join('');

    const grandTotalHtml = designGroups.length > 1
      ? `<div class="grand-total">
          <span>Grand Total</span>
          <span>${detailData.summary.total_received} pcs · ${fmt(detailData.summary.gross_earnings)}</span>
        </div>`
      : '';

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`<!DOCTYPE html>
<html><head>
<title>${pdfTitle}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 20px; color: #333; font-size: 12px; }
  h2 { margin: 0 0 4px; font-size: 18px; }
  .subtitle { color: #666; margin-bottom: 12px; font-size: 13px; }
  .summary-row { display: flex; gap: 16px; margin-bottom: 12px; flex-wrap: wrap; }
  .summary-chip { text-align: center; padding: 6px 12px; background: #f5f5f5; border-radius: 6px; min-width: 80px; }
  .summary-chip .label { font-size: 10px; color: #888; }
  .summary-chip .val { font-size: 14px; font-weight: 700; }
  .design-header { display: flex; align-items: center; gap: 8px; margin: 12px 0 4px; }
  .design-header .dno { font-weight: 700; font-size: 13px; }
  .design-header .part { border: 1px solid #ccc; border-radius: 4px; padding: 1px 6px; font-size: 11px; }
  .design-header .meta { margin-left: auto; font-size: 11px; color: #888; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
  th, td { border: 1px solid #ddd; padding: 4px 6px; text-align: left; font-size: 11px; }
  th { background: #f9f9f9; font-weight: 600; }
  .right { text-align: right; }
  .bold td { font-weight: 700; border-top: 2px solid #999; }
  .grand-total { display: flex; justify-content: space-between; margin-top: 8px; padding: 6px 0; border-top: 2px solid #333; font-weight: 700; font-size: 13px; }
  hr { border: none; border-top: 1px solid #ddd; margin: 8px 0; }
  @media print { body { margin: 10px; } }
</style>
</head><body>
<h2>${detailData.worker_name} (${detailData.worker_id})</h2>
<div class="subtitle">${detailData.worker_type || ''} &middot; ${startDate ? startDate.format('MMMM YYYY') : ''}</div>
${summaryHtml}
${designsHtml}
${grandTotalHtml}
</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 300);
  };

  return (
    <ReportLayout
      title="Worker Performance Report"
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={setStartDate}
      onEndDateChange={setEndDate}
      onExport={() => exportToCsv('worker-performance.csv', filteredData)}
      loading={loading}
      extraFilters={
        <>
          <TextField
            size="small"
            placeholder="Search worker..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 180 }}
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Job Type</InputLabel>
            <Select value={jobType} label="Job Type" onChange={(e) => setJobType(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="STITCHING">Stitching</MenuItem>
              <MenuItem value="PRESS">Press</MenuItem>
            </Select>
          </FormControl>
        </>
      }
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <CircularProgress />
      ) : (
        <DataGrid
          rows={filteredData}
          columns={columns}
          getRowId={(row) => row.worker_id}
          autoHeight
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
          sx={{
            '& .MuiDataGrid-row': { cursor: 'pointer' },
          }}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50, 100]}
        />
      )}

      {/* Worker Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {detailData ? `${detailData.worker_name} (${detailData.worker_id})` : 'Worker Detail'}
          {detailData?.worker_type && (
            <Chip label={detailData.worker_type} size="small" sx={{ ml: 1 }} />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : detailData ? (
            <>
              {/* Summary Cards */}
              <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <SummaryChip label="Received" value={detailData.summary.total_received} />
                <SummaryChip label="Defects" value={detailData.summary.total_defects} color="error" />
                <SummaryChip label="Gross" value={`₹${detailData.summary.gross_earnings.toLocaleString('en-IN')}`} color="success" />
                <SummaryChip label="Advance" value={`₹${detailData.summary.advance_paid.toLocaleString('en-IN')}`} />
                <SummaryChip
                  label="Net Payable"
                  value={`₹${detailData.summary.net_payable.toLocaleString('en-IN')}`}
                  color={detailData.summary.net_payable >= 0 ? 'success' : 'error'}
                />
              </Stack>

              <Divider sx={{ mb: 2 }} />

              {/* Design-wise breakdown */}
                {designGroups.map((group) => (
                  <Box key={`${group.design_no}__${group.garment_part}`} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {group.design_no}
                      </Typography>
                      <Chip label={group.garment_part} size="small" variant="outlined" />
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        {group.totalReceived} pcs · ₹{group.totalEarnings.toLocaleString('en-IN')}
                      </Typography>
                    </Stack>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 40 }}>#</TableCell>
                            <TableCell>Size</TableCell>
                            <TableCell>Issue Date</TableCell>
                            <TableCell>Receive Date</TableCell>
                            <TableCell align="right">Issued</TableCell>
                            <TableCell align="right">Received</TableCell>
                            <TableCell align="right">Defects</TableCell>
                            <TableCell align="right">Rate (₹)</TableCell>
                            <TableCell align="right">Earnings (₹)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {group.sizes.map((s, sIdx) => (
                            <TableRow key={s.assignment_id}>
                              <TableCell>{serialOffset(designGroups, group) + sIdx + 1}</TableCell>
                              <TableCell>{s.size}</TableCell>
                              <TableCell>{formatDate(s.issue_date)}</TableCell>
                              <TableCell>{s.last_receive_date ? formatDate(s.last_receive_date) : '-'}</TableCell>
                              <TableCell align="right">{s.quantity_issued}</TableCell>
                              <TableCell align="right">{s.quantity_received}</TableCell>
                              <TableCell align="right">{s.defect_quantity > 0 ? s.defect_quantity : '-'}</TableCell>
                              <TableCell align="right">{s.rate > 0 ? s.rate : '-'}</TableCell>
                              <TableCell align="right">{s.earnings > 0 ? `₹${s.earnings.toLocaleString('en-IN')}` : '-'}</TableCell>
                            </TableRow>
                          ))}
                          {/* Design subtotal */}
                          {group.sizes.length > 1 && (
                            <TableRow sx={{ '& td': { fontWeight: 600, borderTop: '2px solid', borderColor: 'divider' } }}>
                              <TableCell />
                              <TableCell>Total</TableCell>
                              <TableCell />
                              <TableCell />
                              <TableCell align="right">{group.sizes.reduce((s, r) => s + r.quantity_issued, 0)}</TableCell>
                              <TableCell align="right">{group.totalReceived}</TableCell>
                              <TableCell align="right">{group.totalDefects > 0 ? group.totalDefects : '-'}</TableCell>
                              <TableCell align="right">-</TableCell>
                              <TableCell align="right">₹{group.totalEarnings.toLocaleString('en-IN')}</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}

                {designGroups.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No assignment details found for this period.
                  </Typography>
                )}

                {/* Grand Total */}
                {designGroups.length > 1 && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700}>Grand Total</Typography>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {detailData.summary.total_received} pcs · ₹{detailData.summary.gross_earnings.toLocaleString('en-IN')}
                      </Typography>
                    </Stack>
                  </>
                )}
            </>
          ) : (
            <Alert severity="error">Failed to load worker details.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          {detailData && (
            <Button onClick={handlePrint} startIcon={<PrintIcon />} variant="outlined" size="small">
              Print PDF
            </Button>
          )}
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </ReportLayout>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}

function serialOffset(groups: Array<{ sizes: any[] }>, currentGroup: { sizes: any[] }): number {
  let offset = 0;
  for (const g of groups) {
    if (g === currentGroup) break;
    offset += g.sizes.length;
  }
  return offset;
}

function SummaryChip({ label, value, color }: { label: string; value: string | number; color?: 'success' | 'error' | 'warning' }) {
  return (
    <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, borderRadius: 1, bgcolor: 'grey.50', minWidth: 80 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="subtitle2" fontWeight={700} color={color ? `${color}.main` : 'text.primary'}>
        {value}
      </Typography>
    </Box>
  );
}
